import { useMemo, useState } from 'react';
import useCustomers from '../../../../hooks/useCustomers';
import './InterestTab.scss';
import { useParams } from 'react-router-dom';
import { Loader } from '@piggytechng/component-library';
import dayjs from 'dayjs';
import { PiUser } from 'react-icons/pi';
import { MdAdminPanelSettings } from 'react-icons/md';
import NoData from '../../../../components/NoData/NoData';
import useInterestHistory from '../../../../hooks/useInterestHistory';
import { InterestHistoryItem } from '../../../../types';

const isAdminEntry = (entry: InterestHistoryItem) => entry.action === 'updateuserdata' || !!entry.adminId;

const extractActivityIdFromMemo = (memo?: string) => {
  if (!memo) return '';
  const match = memo.match(/Activity ID:\s*([\w\d]+)/i);
  return match ? match[1] : '';
};

const InterestTab = () => {
  const [activeSubTab, setActiveSubTab] = useState<'setting' | 'history'>('setting');
  const [showModal, setShowModal] = useState<boolean>(false);
  const { setInterest, singleCustomer, isLoading } = useCustomers();
  const { history, isLoading: isLoadingHistory, isLoadingMore, hasMore, loadMore, refetch } = useInterestHistory();
  const params = useParams();
  const id = params.id as string;

  const toggleInterest = async () => {
    const ok = await setInterest(id, singleCustomer?.interestDisabled === 1 ? true : false);
    setShowModal(false);
    if (ok) refetch();
  };

  const interestToggle = !singleCustomer?.interestDisabled;

  // The admin payload doesn't carry an on/off signal, so we derive it from the
  // paired user record (matched by user.uniqueid === admin's Activity ID).
  const userStateByActivityId = useMemo(() => {
    const map = new Map<string, boolean>();
    (history ?? []).forEach((entry) => {
      if (!isAdminEntry(entry) && entry.uniqueid) {
        map.set(entry.uniqueid, entry.action === 'enabled' || entry.value === 1);
      }
    });
    return map;
  }, [history]);

  const sortedHistory = useMemo(() => {
    const items = history ?? [];
    // Hide the user record when an admin record exists for the same toggle
    // (matched by user.uniqueid === admin's Activity ID); admin takes precedence.
    const adminActivityIds = new Set(
      items
        .filter((entry) => isAdminEntry(entry))
        .map((entry) => extractActivityIdFromMemo(entry.memo))
        .filter(Boolean),
    );
    return items
      .filter((entry) => isAdminEntry(entry) || !entry.uniqueid || !adminActivityIds.has(entry.uniqueid))
      .sort((a, b) => (b.date ?? 0) - (a.date ?? 0));
  }, [history]);

  return (
    <>
      <div className="interest-tab">
        {showModal && (
          <div className="confirmation-overlay">
            <div className="confirmation-modal">
              <h1>{`${interestToggle ? 'Disable' : 'Enable'} Wallet Interest`}</h1>
              <p>{`Are you sure you want to ${interestToggle ? 'disable' : 'enable'} user interest?`}</p>
              <div className="buttons-container">
                <button id="cancel-btn" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button
                  id="confirm-btn"
                  style={{
                    backgroundColor: `#1d4ed8`,
                  }}
                  onClick={() => {
                    toggleInterest();
                  }}
                  disabled={isLoading}
                >{`${isLoading ? 'Processing...' : interestToggle ? 'Disable' : 'Enable'}`}</button>
              </div>
            </div>
          </div>
        )}

        <div className="interest-tab__navs">
          <div
            className={`interest-tab__navs__item ${activeSubTab === 'setting' ? 'active' : ''}`}
            onClick={() => setActiveSubTab('setting')}
          >
            Interest Setting
          </div>
          <div
            className={`interest-tab__navs__item ${activeSubTab === 'history' ? 'active' : ''}`}
            onClick={() => setActiveSubTab('history')}
          >
            Toggle History
          </div>
        </div>

        {activeSubTab === 'setting' && (
          <div className="interest-setting">
            <div className="instruction">
              <h1>Enable Interest</h1>
              <p>{`Toggle this option to enable or disable interest earnings on the user's wallet.`}</p>
            </div>
            <div className="toggle-container">
              <div
                id="toggle-outer"
                style={{
                  justifyContent: `${singleCustomer?.interestDisabled !== 1 ? 'end' : 'start'}`,
                  backgroundColor: `${singleCustomer?.interestDisabled !== 1 ? '#21C35D' : '#CBD5E1'}`,
                }}
                onClick={() => {
                  setShowModal(true);
                }}
              >
                <div id="toggle-inner"></div>
              </div>
            </div>
          </div>
        )}

        {activeSubTab === 'history' && (
          <div className="interest-history">
            {isLoadingHistory ? (
              <div className="interest-history__loader">
                <Loader color="#0F60D6" />
              </div>
            ) : sortedHistory.length ? (
              <ul className="interest-history__list">
                {sortedHistory.map((entry, idx) => {
                  const isAdmin = isAdminEntry(entry);
                  const action = isAdmin ? '' : (entry.action ?? '');
                  const activityId = isAdmin ? extractActivityIdFromMemo(entry.memo) : (entry.uniqueid ?? '');
                  // For admin entries, prefer the on/off state derived from the
                  // paired user record (admin payload doesn't include a state).
                  // Fall back to memo parsing if we can't find a match.
                  const isEnabled = isAdmin
                    ? activityId && userStateByActivityId.has(activityId)
                      ? userStateByActivityId.get(activityId)
                      : !/disabled/i.test(entry.memo ?? '')
                    : action === 'enabled' || entry.value === 1;
                  const stateLabel = isEnabled ? 'on' : 'off';
                  const dateLabel = entry.date ? dayjs.unix(entry.date).format('D MMM, YYYY • h:mm A') : '-----';
                  const actorLabel = isAdmin ? 'Admin' : 'User';

                  return (
                    <li
                      key={`${entry.uniqueid ?? entry.rand ?? idx}-${entry.date}-${idx}`}
                      className={`interest-history__item ${
                        isAdmin ? 'interest-history__item--admin' : 'interest-history__item--user'
                      }`}
                    >
                      <div className="interest-history__icon" aria-hidden>
                        {isAdmin ? <MdAdminPanelSettings /> : <PiUser />}
                      </div>

                      <div className="interest-history__body">
                        <p className="interest-history__line">
                          <span className="interest-history__actor">{actorLabel}</span> toggled interest{' '}
                          <span
                            className={`interest-history__state ${
                              isEnabled ? 'interest-history__state--on' : 'interest-history__state--off'
                            }`}
                          >
                            {stateLabel}
                          </span>
                        </p>
                        <p className="interest-history__meta">
                          {activityId && (
                            <span className="interest-history__meta-item">
                              {isAdmin ? 'Activity ID' : 'Reference'}: <code>{activityId}</code>
                            </span>
                          )}
                          {isAdmin && entry.adminId && (
                            <span className="interest-history__meta-item">
                              Admin: <code>{entry.adminId}</code>
                            </span>
                          )}
                        </p>
                      </div>

                      <span className="interest-history__date">{dateLabel}</span>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <NoData message="No interest toggle history yet." />
            )}

            {hasMore && sortedHistory.length > 0 && (
              <div className="interest-history__load-more" onClick={loadMore}>
                {isLoadingMore ? (
                  <Loader color="#0F60D6" maxWidth="50px" />
                ) : (
                  <span className="pointer">Show more</span>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default InterestTab;
